import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import superAdminApiClient from '../../../api/superAdminApiClient';
import CustomEditor from "../../../admin-panel/custom/CustomEditor";
import { toast } from 'react-toastify';
import { Form, Button, Card, Row, Col } from 'react-bootstrap';

const FormInformationPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [validated, setValidated] = useState(false);
    const [initialDescription, setInitialDescription] = useState(isEdit ? null : '');
    const [manualSlug, setManualSlug] = useState(false);

    const [formData, setFormData] = useState({
        page_title: '',
        slug: '',
        description: '',
        status: 'active',
        meta_title: '',
        meta_desc: '',
        meta_keywords: '',
        last_updated_date: new Date().toISOString().slice(0, 10)
    });

    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');

    const fetchPageDetails = React.useCallback(async () => {
        setLoading(true);
        try {
            const response = await superAdminApiClient.get(`/superadmin/web-pages/information`);
            if (response.data.success) {
                // Since superadmin returns a list, let's find our page
                const page = response.data.data.find(p => p.id === parseInt(id));
                if (page) {
                    setFormData({
                        page_title: page.page_title || '',
                        slug: page.slug || '',
                        description: page.description || '',
                        section: page.section || 'information',
                        status: page.status || 'active',
                        meta_title: page.meta_title || '',
                        meta_desc: page.meta_desc || page.meta_description || '',
                        meta_keywords: page.meta_keywords || ''
                    });
                    setInitialDescription(page.description || '');
                    if (page.banner_image) {
                        setImagePreview(page.banner_image);
                    }
                } else {
                    toast.error("Page not found");
                    navigate('/superadmin/web-pages/information');
                }
            } else {
                toast.error("Failed to load page details");
                navigate('/superadmin/web-pages/information');
            }
        } catch (error) {
            console.error('Error fetching page details:', error);
            toast.error("Error loading page details");
            navigate('/superadmin/web-pages/information');
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => {
        if (isEdit) {
            fetchPageDetails();
        }
    }, [isEdit, fetchPageDetails]);

    // Auto-generate safe slug
    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setFormData(prev => {
            const updated = {
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            };

            // Auto-generate slug from title if not manually overridden by the user
            if (name === 'page_title' && !manualSlug && !isEdit) {
                updated.slug = generateSlug(value);
            }

            return updated;
        });
    };

    const handleSlugChange = (e) => {
        setManualSlug(true);
        const rawSlug = e.target.value;
        const cleanSlug = rawSlug.toLowerCase().replace(/[^a-z0-9-/]+/g, '-');
        setFormData(prev => ({
            ...prev,
            slug: cleanSlug
        }));
    };

    const handleDescriptionChange = (data) => {
        setFormData(prev => ({
            ...prev,
            description: data
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        
        if (form.checkValidity() === false || !formData.description.trim()) {
            e.stopPropagation();
            setValidated(true);
            if (!formData.description.trim()) {
                toast.error("Page content / description is required");
            }
            return;
        }

        setSubmitting(true);

        const data = new FormData();
        data.append('page_title', formData.page_title);
        data.append('slug', formData.slug);
        data.append('description', formData.description);
        data.append('status', formData.status);
        data.append('meta_title', formData.meta_title);
        data.append('meta_desc', formData.meta_desc);
        data.append('meta_keywords', formData.meta_keywords);
        data.append('last_updated_date', formData.last_updated_date || new Date().toISOString().slice(0, 10));
        
        if (imageFile) {
            data.append('image', imageFile);
        }

        try {
            let response;
            if (isEdit) {
                response = await superAdminApiClient.put(`/superadmin/web-pages/information/${id}`, data, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                response = await superAdminApiClient.post('/superadmin/web-pages/information', data, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            if (response.data.success) {
                toast.success(isEdit ? "Information page updated successfully!" : "Information page created successfully!");
                navigate('/superadmin/web-pages/information');
            } else {
                toast.error(response.data.message || "Something went wrong.");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to save information page");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex align-items-center justify-content-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-2">
            <Row className="justify-content-center">
                <Col lg={10}>
                    <div className="d-sm-flex align-items-center justify-content-between mb-4">
                        <h4 className="mb-sm-0 text-dark font-weight-bold">{isEdit ? 'Edit Information Page' : 'Create New Information Page'}</h4>
                        <div className="page-title-right">
                            <Link to="/superadmin/web-pages/information" className="btn btn-outline-dark btn-sm">
                                <i className="ri-arrow-left-line align-bottom me-1"></i> Back to List
                            </Link>
                        </div>
                    </div>

                    <Card className="border-0 shadow-sm" style={{ borderRadius: '12px', background: '#ffffff' }}>
                        <Card.Body className="p-4 text-dark">
                            <Form noValidate validated={validated} onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={8}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fs-14 font-weight-semibold text-dark">Page Title <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="page_title"
                                                value={formData.page_title}
                                                onChange={handleInputChange}
                                                placeholder="e.g. Terms of Service, User Agreement"
                                                required
                                                className="border-grey-subtle text-dark"
                                                style={{ border: '1px solid #ced4da', color: '#212529', background: '#fff' }}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Please provide a page title.
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="fs-14 font-weight-semibold text-dark">Page Slug <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="slug"
                                                value={formData.slug}
                                                onChange={handleSlugChange}
                                                placeholder="auto-generated-slug-from-title"
                                                required
                                                className="border-grey-subtle text-dark"
                                                style={{ border: '1px solid #ced4da', color: '#212529', background: '#fff' }}
                                            />
                                            <Form.Text className="text-muted">
                                                The URL slug used to access this page. Hyphens, lowercase letters and numbers only.
                                            </Form.Text>
                                            <Form.Control.Feedback type="invalid">
                                                A unique slug is required.
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="fs-14 font-weight-semibold text-dark">Page Content <span className="text-danger">*</span></Form.Label>
                                            <div className="rich-editor-wrapper bg-white rounded text-dark" style={{ border: '1px solid #ced4da' }}>
                                                {initialDescription !== null && (
                                                    <CustomEditor 
                                                        data={initialDescription}
                                                        onChange={handleDescriptionChange}
                                                        placeholder="Draft your detailed information page content here using CKEditor..."
                                                    />
                                                )}
                                            </div>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fs-14 font-weight-semibold text-dark font-medium">Settings</Form.Label>
                                            <div className="p-3 rounded d-flex flex-column gap-3" style={{ background: '#f8f9fa', border: '1px solid #e9ecef' }}>
                                                <Form.Check 
                                                    type="switch"
                                                    id="form-page-status"
                                                    label="Active / Visible on Footer"
                                                    checked={formData.status === 'active'}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.checked ? 'active' : 'inactive' }))}
                                                    style={{ cursor: 'pointer' }}
                                                    className="text-dark font-medium"
                                                />
                                            </div>
                                        </Form.Group>

                                        {/* Last updated date is automatically set to current date */}

                                        <Form.Group className="mb-4">
                                            <Form.Label className="fs-14 font-weight-semibold text-dark">Banner / Cover Image (Optional)</Form.Label>
                                            <Form.Control
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                className="border-grey-subtle text-dark"
                                                style={{ border: '1px solid #ced4da', color: '#212529', background: '#fff' }}
                                            />
                                            <div className="mt-3 text-center border border-dashed rounded p-2" style={{ border: '1px dashed #ced4da', minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa' }}>
                                                {imagePreview ? (
                                                    <img 
                                                        src={imagePreview} 
                                                        alt="Preview" 
                                                        className="img-fluid rounded" 
                                                        style={{ maxHeight: '150px', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <span className="text-muted fs-12">No banner selected</span>
                                                )}
                                            </div>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr className="my-4" style={{ borderColor: '#e9ecef' }} />

                                <h5 className="mb-3 text-primary font-weight-bold">Search Engine Optimization (SEO)</h5>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fs-14 font-weight-semibold text-dark">Meta Title</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="meta_title"
                                                value={formData.meta_title}
                                                onChange={handleInputChange}
                                                placeholder="Optimal SEO Title (50-60 characters)"
                                                className="border-grey-subtle text-dark"
                                                style={{ border: '1px solid #ced4da', color: '#212529', background: '#fff' }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fs-14 font-weight-semibold text-dark">Meta Description</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                name="meta_desc"
                                                value={formData.meta_desc}
                                                onChange={handleInputChange}
                                                placeholder="Brief summary for search result snippets (150-160 characters)"
                                                className="border-grey-subtle text-dark"
                                                style={{ border: '1px solid #ced4da', color: '#212529', background: '#fff' }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fs-14 font-weight-semibold text-dark">Meta Keywords</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="meta_keywords"
                                                value={formData.meta_keywords}
                                                onChange={handleInputChange}
                                                placeholder="e.g. terms of service, policies, user agreement (comma separated)"
                                                className="border-grey-subtle text-dark"
                                                style={{ border: '1px solid #ced4da', color: '#212529', background: '#fff' }}
                                            />
                                            <Form.Text className="text-muted" style={{ fontSize: '12px' }}>
                                                Separate keywords with commas for best SEO results.
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="d-flex gap-2 justify-content-end mt-4">
                                    <Link to="/superadmin/web-pages/information" className="btn btn-light border px-4">Cancel</Link>
                                    <Button 
                                        type="submit" 
                                        variant="primary" 
                                        className="px-4 d-flex align-items-center gap-1"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-save-line fs-16"></i> {isEdit ? 'Save Changes' : 'Publish Page'}
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default FormInformationPage;
